import { useEffect, useMemo, useState } from "react";
import { Search, Library as LibraryIcon } from "lucide-react";
import { api, supporterHeaders } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import PaperCard from "@/components/PaperCard";
import Reveal from "@/components/Reveal";

export default function Library() {
  const { unlocked } = useSite();
  const [papers, setPapers] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (category) params.category = category;
    api.get("/papers", { ...supporterHeaders(), params })
      .then((r) => setPapers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, category, unlocked]);

  const categories = useMemo(() => {
    const set = new Set(papers.map((p) => p.category));
    return Array.from(set);
  }, [papers]);

  return (
    <main className="min-h-screen">
      <section className="relative hero-glow py-16 sm:py-20 border-b border-[#1E2638]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-sky-400 mb-4">The Archive</p>
          <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl">
            White Paper Library
          </h1>
          <p className="mt-4 text-slate-400 max-w-2xl leading-relaxed">
            Every paper is free to read. Supporters-only deep dives unlock with a
            donation or an access code.
          </p>

          <div className="mt-10 flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search titles, abstracts, tags…"
                data-testid="search-input"
                className="w-full bg-[#131824] border border-[#1E2638] rounded-full pl-10 pr-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-sky-400/60 transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setCategory("")}
                data-testid="filter-category-all"
                className={`font-mono text-[11px] uppercase tracking-[0.15em] rounded-full px-4 py-2 border transition-colors ${
                  !category
                    ? "bg-sky-400 text-[#07090E] border-sky-400 font-semibold"
                    : "border-[#1E2638] text-slate-400 hover:border-sky-400/50 hover:text-sky-300"
                }`}
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(category === c ? "" : c)}
                  data-testid={`filter-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`font-mono text-[11px] uppercase tracking-[0.15em] rounded-full px-4 py-2 border transition-colors ${
                    category === c
                      ? "bg-sky-400 text-[#07090E] border-sky-400 font-semibold"
                      : "border-[#1E2638] text-slate-400 hover:border-sky-400/50 hover:text-sky-300"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500 py-20 text-center">
              Loading archive…
            </p>
          ) : papers.length === 0 ? (
            <div className="py-24 text-center">
              <LibraryIcon className="w-10 h-10 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-400">No papers match your search.</p>
            </div>
          ) : (
            <div data-testid="paper-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {papers.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.1}>
                  <PaperCard paper={p} index={i} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
